import { gql, useQuery, useMutation } from '@apollo/client';
import React, { useRef, useState } from 'react';

import { Icon, IconDelete } from '@flixbus/honeycomb-icons-react';
import {
	Panel,
	PanelHeader,
	PanelContent,
	Box,
	Tag,
	Button,
	Heading,
	Popup,
	PopupSection,
	Text,
} from '@flixbus/honeycomb-react';

import Loader from 'common/Loader';
import { formatDateDisplay, isTimeStampInPast, sortByDateUsingTimestamp } from 'utils/datetime';

import styles from './styles.module.css';

const FETCH_EMPLOYMENT_LOG = gql`
	query GetCrewMemberEmployment($crewMemberId: ID!) {
		crewMemberEmploymentTypes(crewMemberId: $crewMemberId) {
			id
			employmentType
			effectiveTimestamp
		}
	}
`;

const DELETE_EMPLOYMENT = gql`
	mutation deleteEmploymentRecord($baseId: ID!) {
		deleteEmploymentType(id: $baseId) {
			... on CrewMemberEmploymentTypeSuccessResponse {
				employmentType {
					id
				}
			}
			... on CrewMemberEmploymentTypeErrorResponse {
				errors
			}
		}
	}
`;

const EMPLOYMENT_TYPE_LABEL = {
	fullTime: 'Full time',
	partTime: 'Part time',
};

/**
 * PROPS (ActiveElements + page):
 * - employeeId: string (required)
 * - currentEmploymentTypeId?: string | null   // to mark the "Current" record
 * - onClose: () => void
 * - onError?: () => void
 */
export default function EmploymentLog({
	employeeId,
	currentEmploymentTypeId = null,
	onClose,
	onError = () => {},
}) {
	const targetRef = useRef(null);

	const [employmentTypeRecords, setEmploymentTypeRecords] = useState([]);
	const [warnModalShown, setWarnModalShown] = useState(false);
	const [currentlyDeleting, setCurrentlyDeleting] = useState('');

	const { loading: queryLoading } = useQuery(FETCH_EMPLOYMENT_LOG, {
		variables: { crewMemberId: employeeId },
		onCompleted: (data) => {
			const employments = data.crewMemberEmploymentTypes || [];
			const sorted = [...employments]
				.sort((a, b) => sortByDateUsingTimestamp(a.effectiveTimestamp, b.effectiveTimestamp))
				.map((employment) => ({
					...employment,
					effectiveTimestamp: formatDateDisplay(employment.effectiveTimestamp),
				}));
			setEmploymentTypeRecords(sorted);
		},
		onError: () => onError(),
		fetchPolicy: 'no-cache',
		notifyOnNetworkStatusChange: true,
	});

	const [deleteEmploymentRecord, { loading: mutationLoading }] = useMutation(DELETE_EMPLOYMENT, {
		refetchQueries: ['GetCrewMemberEmployment'],
		onCompleted: () => setCurrentlyDeleting(''),
		onError: () => onError(),
		fetchPolicy: 'no-cache',
	});

	const warnDeletion = (id) => {
		setWarnModalShown(true);
		setCurrentlyDeleting(id);
	};

	const onDeleteEmploymentRecord = () => {
		setWarnModalShown(false);
		deleteEmploymentRecord({ variables: { baseId: currentlyDeleting } });
	};

	const onCancelDeletion = () => {
		setWarnModalShown(false);
		setCurrentlyDeleting('');
	};

	const getRelativeTimeLabelForRecord = (id, effectiveTimestamp) => {
		if (currentEmploymentTypeId && currentEmploymentTypeId === id) {
			return 'Current';
		}
		if (isTimeStampInPast(effectiveTimestamp)) return 'Past';
		return 'Future';
	};

	const renderEmployments = () => {
		if (queryLoading) return null;
		if (employmentTypeRecords.length === 0) return <div>No employment type history.</div>;

		return employmentTypeRecords.map((employment) => {
			const { id, employmentType, effectiveTimestamp } = employment;
			const label = EMPLOYMENT_TYPE_LABEL[employmentType] ?? employmentType;
			const timeRel = getRelativeTimeLabelForRecord(id, effectiveTimestamp);

			return (
				<Box key={id} extraClasses={styles.boxpadding}>
					<div className={styles.row}>
						<Heading extraClasses={styles.heading} size={4} flushSpace>
							{label}
						</Heading>
						<Tag
							display="subtle"
							{...(timeRel === 'Current' ? { appearance: 'success' } : {})}
							small
						>
							{timeRel}
						</Tag>

						{timeRel === 'Future' && (
							<Button
								display="square"
								aria-label="Delete entry"
								appearance="link"
								onClick={() => warnDeletion(id)}
								loading={mutationLoading && currentlyDeleting === id}
							>
								{!mutationLoading && <Icon InlineIcon={IconDelete} aria-hidden="true" size={4} />}
							</Button>
						)}
					</div>

					<div className={styles.row}>
						<span className={styles.date}>
							Effective date:
							<br /> {effectiveTimestamp}
						</span>
					</div>
				</Box>
			);
		});
	};

	return (
		<>
			<Panel
				id="employment-panel"
				active={true}
				hasOverlay
				onOverlayClick={onClose}
				targetRef={targetRef}
			>
				<PanelHeader closeButtonProps={{ label: 'Close', onClick: onClose }}>
					Employment History
				</PanelHeader>
				<PanelContent>
					{renderEmployments()}
					{queryLoading && <Loader />}
				</PanelContent>
			</Panel>

			<Popup id="delete-employment" style={{ zIndex: '10000' }} active={warnModalShown}>
				<PopupSection>
					<h1>Confirm deletion</h1>
				</PopupSection>
				<PopupSection>
					<Text>You're about to delete this item permanently. Do you want to proceed?</Text>
				</PopupSection>
				<PopupSection>
					<Button onClick={onCancelDeletion}>Cancel</Button>
					<Button onClick={onDeleteEmploymentRecord} appearance="danger">
						Delete
					</Button>
				</PopupSection>
			</Popup>
		</>
	);
}
